import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../contexts/ApiContext';
import PropertyNicknameChip from './PropertyNicknameChip';

type ThemeTag = {
  id?: number;
  theme_key?: string;
  theme_label?: string;
  status?: string;
  source?: string;
  confidence?: number | null;
};

type CommsMessage = {
  id: number;
  position?: number;
  sender_role?: string;
  sender_name?: string | null;
  sent_at_raw?: string | null;
  message_kind?: string;
  body?: string;
  themes?: ThemeTag[];
};

type CommsThread = {
  id: number;
  confirmation_code: string;
  unified_booking_id?: number | null;
  property_nickname?: string | null;
  guest_display_name?: string | null;
  parse_status?: string;
  message_count?: number;
  messages?: CommsMessage[];
};

type ThemeOption = { key: string; label: string };

function collectThreadThemes(thread: CommsThread): ThemeTag[] {
  const out: ThemeTag[] = [];
  for (const m of thread.messages || []) {
    for (const t of m.themes || []) {
      if (t.status === 'rejected') continue;
      out.push(t);
    }
  }
  return out;
}

function groupKey(thread: CommsThread): string {
  if (thread.unified_booking_id != null) return `booking:${thread.unified_booking_id}`;
  return `code:${thread.confirmation_code}`;
}

function formatThemeChipLabel(t: {
  theme_key?: string;
  theme_label?: string;
  confidence?: number | null;
}): string {
  const raw = (t.theme_label || t.theme_key || 'theme').trim();
  const name = raw.includes('_')
    ? raw
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : raw;
  if (t.confidence == null || Number.isNaN(Number(t.confidence))) return name;
  return `${name} (${Number(t.confidence).toFixed(2)})`;
}

const BookingCommsTab: React.FC = () => {
  const [threads, setThreads] = useState<CommsThread[]>([]);
  const [themes, setThemes] = useState<ThemeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filterCode, setFilterCode] = useState('');
  const [filterGuest, setFilterGuest] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterThemeKey, setFilterThemeKey] = useState('');
  const [filterThemeStatus, setFilterThemeStatus] = useState<'any' | 'suggested' | 'accepted'>('any');
  const [filterMinConfidence, setFilterMinConfidence] = useState('');
  const [filterSender, setFilterSender] = useState<'any' | 'guest' | 'host'>('any');

  const loadWith = useCallback(
    async (overrides?: {
      code?: string;
      guest?: string;
      property?: string;
      themeKey?: string;
      themeStatus?: 'any' | 'suggested' | 'accepted';
      minConfidence?: string;
    }) => {
      const code = overrides?.code ?? filterCode;
      const guest = overrides?.guest ?? filterGuest;
      const property = overrides?.property ?? filterProperty;
      const themeKey = overrides?.themeKey ?? filterThemeKey;
      const themeStatus = overrides?.themeStatus ?? filterThemeStatus;
      const minConfidence = overrides?.minConfidence ?? filterMinConfidence;

      setLoading(true);
      setError('');
      try {
        const params: Record<string, string | number> = { limit: 300 };
        if (code.trim()) params.confirmation_code = code.trim().toUpperCase();
        if (guest.trim()) params.guest = guest.trim();
        if (property.trim()) params.property_nickname = property.trim();
        if (themeKey) params.theme_key = themeKey;
        if (themeStatus !== 'any') params.theme_status = themeStatus;
        if (minConfidence.trim() !== '') {
          const n = Number(minConfidence);
          if (Number.isFinite(n)) params.min_confidence = n;
        }
        const [threadsRes, themesRes] = await Promise.all([
          api.get('/api/airbnb-messages/threads', { params, timeout: 60000 }),
          api.get('/api/airbnb-messages/themes', { timeout: 30000 }),
        ]);
        setThreads(threadsRes.data?.threads || []);
        setThemes(
          (themesRes.data?.themes || []).map((t: any) => ({
            key: t.key,
            label: t.label || t.key,
          }))
        );
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load communications');
        setThreads([]);
      } finally {
        setLoading(false);
      }
    },
    [filterCode, filterGuest, filterProperty, filterThemeKey, filterThemeStatus, filterMinConfidence]
  );

  // Initial load only; subsequent filter changes apply on Refresh.
  useEffect(() => {
    void loadWith();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredThreads = useMemo(() => {
    if (filterSender === 'any') return threads;
    return threads
      .map((t) => ({
        ...t,
        messages: (t.messages || []).filter((m) => m.sender_role === filterSender),
      }))
      .filter((t) => (t.messages || []).length > 0);
  }, [threads, filterSender]);

  const groups = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        bookingId: number | null;
        confirmationCodes: Set<string>;
        property: string;
        guest: string;
        threads: CommsThread[];
      }
    >();
    for (const t of filteredThreads) {
      const key = groupKey(t);
      if (!map.has(key)) {
        map.set(key, {
          key,
          bookingId: t.unified_booking_id ?? null,
          confirmationCodes: new Set(),
          property: t.property_nickname || '—',
          guest: t.guest_display_name || '—',
          threads: [],
        });
      }
      const g = map.get(key)!;
      g.threads.push(t);
      if (t.confirmation_code) g.confirmationCodes.add(t.confirmation_code);
      if (t.property_nickname) g.property = t.property_nickname;
      if (t.guest_display_name) g.guest = t.guest_display_name;
    }
    return Array.from(map.values()).sort((a, b) => {
      const aId = a.bookingId ?? 0;
      const bId = b.bookingId ?? 0;
      return bId - aId;
    });
  }, [filteredThreads]);

  const clearFilters = () => {
    setFilterCode('');
    setFilterGuest('');
    setFilterProperty('');
    setFilterThemeKey('');
    setFilterThemeStatus('any');
    setFilterMinConfidence('');
    setFilterSender('any');
    void loadWith({
      code: '',
      guest: '',
      property: '',
      themeKey: '',
      themeStatus: 'any',
      minConfidence: '',
    });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        All pasted Airbnb host-inbox threads, grouped by booking. Theme chips show confidence; dashed = suggested,
        solid green = accepted. Open a booking to paste new threads or re-run LLM tagging.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Confirmation code"
            value={filterCode}
            onChange={(e) => setFilterCode(e.target.value)}
            placeholder="HMYKK…"
          />
          <TextField
            size="small"
            label="Guest"
            value={filterGuest}
            onChange={(e) => setFilterGuest(e.target.value)}
          />
          <TextField
            size="small"
            label="Property"
            value={filterProperty}
            onChange={(e) => setFilterProperty(e.target.value)}
            placeholder="28LC"
          />
          <FormControl size="small">
            <InputLabel id="comms-theme-label">Theme</InputLabel>
            <Select
              labelId="comms-theme-label"
              label="Theme"
              value={filterThemeKey}
              onChange={(e) => setFilterThemeKey(String(e.target.value))}
            >
              <MenuItem value="">Any theme</MenuItem>
              {themes.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="comms-status-label">Theme status</InputLabel>
            <Select
              labelId="comms-status-label"
              label="Theme status"
              value={filterThemeStatus}
              onChange={(e) => setFilterThemeStatus(e.target.value as any)}
            >
              <MenuItem value="any">Any status</MenuItem>
              <MenuItem value="suggested">Suggested</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Min confidence"
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.05 }}
            value={filterMinConfidence}
            onChange={(e) => setFilterMinConfidence(e.target.value)}
            placeholder="0.8"
          />
          <FormControl size="small">
            <InputLabel id="comms-sender-label">Sender</InputLabel>
            <Select
              labelId="comms-sender-label"
              label="Sender"
              value={filterSender}
              onChange={(e) => setFilterSender(e.target.value as any)}
            >
              <MenuItem value="any">Any sender</MenuItem>
              <MenuItem value="guest">Guest only</MenuItem>
              <MenuItem value="host">Host only</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={() => void loadWith()} disabled={loading}>
            Apply / Refresh
          </Button>
          <Button variant="outlined" size="small" onClick={clearFilters} disabled={loading}>
            Clear filters
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {groups.length} booking group{groups.length === 1 ? '' : 's'} · {filteredThreads.length} thread
            {filteredThreads.length === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && groups.length === 0 && (
        <Alert severity="warning">
          No communications match these filters. Paste a thread from a booking details page first.
        </Alert>
      )}

      {!loading &&
        groups.map((g) => {
          const themeSummary = new Map<string, { maxConf: number; statuses: Set<string> }>();
          for (const t of g.threads) {
            for (const tag of collectThreadThemes(t)) {
              const key = tag.theme_key || 'other';
              const prev = themeSummary.get(key) || { maxConf: 0, statuses: new Set<string>() };
              prev.maxConf = Math.max(prev.maxConf, Number(tag.confidence || 0));
              if (tag.status) prev.statuses.add(tag.status);
              themeSummary.set(key, prev);
            }
          }
          return (
            <Accordion key={g.key} defaultExpanded={groups.length <= 3} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', pr: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={600} component="span">
                      {g.guest}
                    </Typography>
                    <PropertyNicknameChip nickname={g.property === '—' ? null : g.property} />
                    {g.bookingId != null ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={`Booking #${g.bookingId}`}
                        component={RouterLink}
                        to={`/bookings/${g.bookingId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    ) : (
                      <Chip size="small" label="Unlinked booking" />
                    )}
                    {Array.from(g.confirmationCodes).map((c) => (
                      <Chip key={c} size="small" variant="outlined" label={c} />
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      {g.threads.length} thread{g.threads.length === 1 ? '' : 's'} ·{' '}
                      {g.threads.reduce((n, t) => n + (t.messages?.length || 0), 0)} messages
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {Array.from(themeSummary.entries())
                      .sort((a, b) => b[1].maxConf - a[1].maxConf)
                      .map(([key, info]) => (
                        <Chip
                          key={key}
                          size="small"
                          variant={info.statuses.has('accepted') ? 'filled' : 'outlined'}
                          label={formatThemeChipLabel({
                            theme_key: key,
                            confidence: info.maxConf || null,
                          })}
                          sx={{
                            height: 26,
                            fontWeight: 500,
                            fontSize: '0.72rem',
                            borderRadius: '8px',
                            bgcolor: info.statuses.has('accepted')
                              ? 'rgba(46, 125, 50, 0.14)'
                              : 'rgba(25, 118, 210, 0.06)',
                            color: info.statuses.has('accepted') ? '#1b5e20' : '#1565c0',
                            borderColor: info.statuses.has('accepted')
                              ? 'rgba(46, 125, 50, 0.45)'
                              : 'rgba(25, 118, 210, 0.45)',
                            borderStyle: info.statuses.has('accepted') ? 'solid' : 'dashed',
                          }}
                        />
                      ))}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {g.threads.map((thread) => (
                  <Box key={thread.id} sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Thread #{thread.id} · {thread.confirmation_code} · {thread.parse_status} ·{' '}
                      {thread.message_count || thread.messages?.length || 0} messages
                      {g.bookingId != null && (
                        <>
                          {' · '}
                          <Typography
                            component={RouterLink}
                            to={`/bookings/${g.bookingId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{ color: 'primary.main' }}
                          >
                            Open booking
                          </Typography>
                        </>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {(thread.messages || []).map((m) => (
                        <Paper
                          key={m.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor:
                              m.sender_role === 'guest'
                                ? 'rgba(25, 118, 210, 0.06)'
                                : m.sender_role === 'host'
                                  ? 'rgba(0, 0, 0, 0.03)'
                                  : 'transparent',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            #{m.position} · {m.sender_role}
                            {m.sender_name ? ` · ${m.sender_name}` : ''}
                            {m.sent_at_raw ? ` · ${m.sent_at_raw}` : ''}
                            {m.message_kind && m.message_kind !== 'chat' ? ` · ${m.message_kind}` : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, mb: 1 }}>
                            {m.body}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {(m.themes || [])
                              .filter((t) => t.status !== 'rejected')
                              .map((t) => {
                                const accepted = t.status === 'accepted';
                                const suggested = t.status === 'suggested';
                                return (
                                  <Chip
                                    key={t.id || `${t.theme_key}-${t.status}`}
                                    size="small"
                                    variant={accepted ? 'filled' : 'outlined'}
                                    label={formatThemeChipLabel(t)}
                                    sx={{
                                      height: 26,
                                      fontWeight: 500,
                                      fontSize: '0.72rem',
                                      borderRadius: '8px',
                                      bgcolor: accepted
                                        ? 'rgba(46, 125, 50, 0.14)'
                                        : suggested
                                          ? 'rgba(25, 118, 210, 0.06)'
                                          : 'rgba(0,0,0,0.04)',
                                      color: accepted ? '#1b5e20' : suggested ? '#1565c0' : 'text.secondary',
                                      borderColor: accepted
                                        ? 'rgba(46, 125, 50, 0.45)'
                                        : suggested
                                          ? 'rgba(25, 118, 210, 0.45)'
                                          : 'divider',
                                      borderStyle: suggested ? 'dashed' : 'solid',
                                    }}
                                  />
                                );
                              })}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
    </Box>
  );
};

export default BookingCommsTab;
